import { useState, useEffect, useCallback } from 'react';
import { MenuItem } from '../types';

export const useProductOptions = (item: MenuItem | null, isOpen: boolean, initialQuantity: number = 1, initialNotes: string = '') => {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Record<string, number>>>({});
  const [notes, setNotes] = useState(initialNotes);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Initialize selected options when modal opens
  useEffect(() => {
    if (isOpen && item) {
      setIsDescriptionExpanded(false);
      const initialSelected: Record<string, Record<string, number>> = {};
      item.extras?.forEach(extra => {
        initialSelected[extra.id] = {};
      });
      setSelectedOptions(initialSelected);
      setNotes(initialNotes);
      setQuantity(initialQuantity);
    }
  }, [isOpen, item, initialQuantity, initialNotes]);

  const hasPromotion = item?.comercial?.promotionActive && (item?.comercial?.promotionalPrice || 0) < (item?.price || 0);
  const itemBasePrice = hasPromotion ? item?.comercial?.promotionalPrice || 0 : item?.price || 0;

  // Calculate total price based on selected options and quantity
  const calculateTotalPrice = useCallback(() => {
    if (!item) return 0;
    
    let extrasTotal = 0;
    Object.entries(selectedOptions).forEach(([extraId, options]) => {
      const extraGroup = item.extras?.find(e => e.id === extraId);
      if (extraGroup) {
        Object.entries(options).forEach(([optionId, qty]) => {
          const option = extraGroup.options.find(o => o.id === optionId);
          if (option && option.price) {
            extrasTotal += option.price * qty;
          }
        });
      }
    });

    return (itemBasePrice + extrasTotal) * quantity;
  }, [item, selectedOptions, quantity, itemBasePrice]);

  const toggleOption = (extraGroupId: string, optionId: string, maxQuantity: number = 1, minQuantity: number = 0, isRequired: boolean = false) => {
    setSelectedOptions(prev => {
      const groupOptions = { ...prev[extraGroupId] } || {};
      const currentQty = groupOptions[optionId] || 0;

      // Handle single selection (max 1)
      if (maxQuantity === 1) {
        if (currentQty > 0) {
          delete groupOptions[optionId];
        } else {
          // Clear other options in group if max is 1
          Object.keys(groupOptions).forEach(id => delete groupOptions[id]);
          groupOptions[optionId] = 1;
        }
      } else {
        // Handle multiple selection
        const totalInGroup = Object.values(groupOptions).reduce((sum, q) => sum + q, 0);
        
        if (currentQty > 0) {
          delete groupOptions[optionId];
        } else if (totalInGroup < maxQuantity) {
          groupOptions[optionId] = 1;
        }
      }

      return { ...prev, [extraGroupId]: groupOptions };
    });
  };

  const isGroupValid = useCallback((extraGroupId: string) => {
    if (!item) return true;
    const extraGroup = item.extras?.find(e => e.id === extraGroupId);
    if (!extraGroup || !extraGroup.required) return true;

    const groupOptions = selectedOptions[extraGroupId] || {};
    const totalSelected = Object.values(groupOptions).reduce((sum, q) => sum + q, 0);
    return totalSelected >= (extraGroup.minQuantity || 1);
  }, [item, selectedOptions]);

  const areAllGroupsValid = useCallback(() => {
    if (!item || !item.extras) return true;
    return item.extras.every(extra => !extra.required || isGroupValid(extra.id));
  }, [item, isGroupValid]);

  return {
    selectedOptions,
    setSelectedOptions,
    notes,
    setNotes,
    quantity,
    setQuantity,
    isDescriptionExpanded,
    setIsDescriptionExpanded,
    totalPrice: calculateTotalPrice(),
    toggleOption,
    isGroupValid,
    areAllGroupsValid
  };
};
